/**
 * pedal_profile — 페달 위치 → 출력값 매핑 곡선(순수 함수).
 *
 * 기본은 선형(밟은 만큼 그대로)이지만, 운전자가 제어점을 드래그해 초반을 완만하게(정밀
 * 조작) 하거나 급하게(즉답성) 만들 수 있다. 가속/브레이크를 각각 따로 설정한다.
 *
 * 설계 결정(사용자 확인):
 *  - 콕핏 공통 프로파일(차량별 아님)
 *  - 가속·브레이크 모두 편집 가능
 *  - 차량에 전송하지 않는다 — 콕핏에서 변환해 기존 명령 그대로 보낸다
 *  - 제어점 5개, x 는 균등 고정(0/0.25/0.5/0.75/1)이고 y 만 드래그
 *  - 마지막 점(x=1)의 y = "최고값" — 끝까지 밟았을 때의 출력 상한을 이 그래프에서 정한다
 */

export interface PedalCurvePoint {
  /** 페달 위치 0..1 (균등 고정) */
  x: number;
  /** 출력 비율 0..1 */
  y: number;
}

export interface PedalProfile {
  accel: PedalCurvePoint[];
  brake: PedalCurvePoint[];
}

export type PedalKind = "accel" | "brake";

/** 제어점 개수(사용자 확인: 5개). x 는 균등 분할로 고정된다. */
export const PEDAL_PROFILE_POINTS = 5;

/** 최고값(마지막 점)이 0 이 되면 페달이 완전히 죽으므로 하한을 둔다. */
export const PEDAL_PROFILE_MIN_MAX_OUTPUT = 0.1;

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/** 기본(선형) 프로파일 — 밟은 만큼 그대로 나간다. */
export function createLinearCurve(): PedalCurvePoint[] {
  return Array.from({ length: PEDAL_PROFILE_POINTS }, (_, i) => {
    const x = i / (PEDAL_PROFILE_POINTS - 1);
    return { x, y: x };
  });
}

export function createDefaultPedalProfile(): PedalProfile {
  return { accel: createLinearCurve(), brake: createLinearCurve() };
}

/**
 * 제어점 하나의 y 를 옮긴다(드래그).
 *
 * 단조증가를 강제한다 — 더 밟았는데 출력이 줄어드는 곡선은 위험하므로 앞뒤 점을 함께 민다.
 * 첫 점(x=0)은 항상 0 으로 고정(떼면 반드시 0). 마지막 점은 최고값이라 하한만 둔다.
 */
export function setCurvePointY(
  curve: PedalCurvePoint[],
  index: number,
  nextY: number,
): PedalCurvePoint[] {
  if (index <= 0 || index >= curve.length) {
    // 첫 점은 이동 불가(0 고정). 범위 밖 인덱스는 무시.
    return curve;
  }

  const isLast = index === curve.length - 1;
  const y = isLast
    ? Math.max(PEDAL_PROFILE_MIN_MAX_OUTPUT, clamp01(nextY))
    : clamp01(nextY);

  const next = curve.map((p) => ({ ...p }));
  next[index].y = y;

  // 단조증가 유지 — 왼쪽은 y 이하로, 오른쪽은 y 이상으로 밀어낸다.
  for (let i = index - 1; i >= 1; i -= 1) {
    if (next[i].y > next[i + 1].y) next[i].y = next[i + 1].y;
  }
  for (let i = index + 1; i < next.length; i += 1) {
    if (next[i].y < next[i - 1].y) next[i].y = next[i - 1].y;
  }
  next[0].y = 0;

  return next;
}

/** 곡선의 최고값(끝까지 밟았을 때의 출력 비율). */
export function getCurveMaxOutput(curve: PedalCurvePoint[]): number {
  return curve.length > 0 ? curve[curve.length - 1].y : 1;
}

/** 최고값만 바꾼다(마지막 점 이동과 동일). */
export function setCurveMaxOutput(
  curve: PedalCurvePoint[],
  maxOutput: number,
): PedalCurvePoint[] {
  return setCurvePointY(curve, curve.length - 1, maxOutput);
}

/**
 * 페달 위치(0..1)를 곡선에 통과시켜 출력 비율(0..1)을 얻는다.
 * 제어점 사이는 선형 보간 — 단조증가가 보장되므로 뒤집힘이 없다.
 */
export function applyPedalCurve(
  curve: PedalCurvePoint[],
  input: number,
): number {
  const points = Array.isArray(curve) && curve.length >= 2 ? curve : createLinearCurve();
  const x = clamp01(input);

  for (let i = 1; i < points.length; i += 1) {
    const left = points[i - 1];
    const right = points[i];
    if (x <= right.x) {
      const span = right.x - left.x;
      if (span <= 0) return clamp01(right.y);
      const t = (x - left.x) / span;
      return clamp01(left.y + (right.y - left.y) * t);
    }
  }

  return clamp01(points[points.length - 1].y);
}

/**
 * 부호 있는 페달 명령(-100..100, 양수=가속 / 음수=브레이크)에 프로파일을 적용한다.
 * 부호는 유지하고 크기만 곡선으로 바꾼다.
 */
export function applyPedalProfileToSigned(
  profile: PedalProfile,
  signedPedal: number,
  scale: number = 100,
): number {
  const value = Number(signedPedal);
  if (!Number.isFinite(value) || value === 0) {
    return 0;
  }

  const magnitude = Math.min(1, Math.abs(value) / scale);
  const curve = value > 0 ? profile.accel : profile.brake;
  const mapped = applyPedalCurve(curve, magnitude);
  return Math.sign(value) * mapped * scale;
}
